// Los textos del módulo, en castellano.
//
// Viajan CON el módulo, en los cuatro idiomas, incluidas las claves de las
// pantallas de la plataforma que hablan de él —su nombre en el menú, sus
// permisos en Roles, sus ajustes, sus acciones en Auditoría, la fila de correo
// saliente y sus arranques de conversación en el asistente—. Un módulo que
// obliga a añadir claves al shell no es extraíble (§4.2), y el test
// `platform/web/tests/i18nCoverage.test.js` exige que los cuatro ficheros
// tengan EXACTAMENTE el mismo juego de claves.
//
// Dos bloques merecen una nota:
//
//   · `quality` son los avisos sobre los DATOS (§43). El backend manda un
//     código y sus parámetros, nunca una frase; aquí se convierte en algo que
//     se lee. Un código sin traducir sale por `generic`, que dice el código:
//     feo, pero visible, que es lo que hay que ser.
//   · `findings` son los hallazgos que levantan los módulos. Misma regla, y por
//     eso están aquí y no en cada módulo: la frase la escribe quien la enseña.

export default {
  errors: {
    REPORT_SCHEDULE_OWNER_ONLY: 'Un informe programado se ejecuta con los permisos de quien lo creó: solo esa persona puede cambiarlo.',
  },
  // Como se llama este modulo en las pantallas de la plataforma (el
  // selector de la demo, Roles, Auditoria). Sin esto salen listando el
  // slug en ingles, que se lee como un modulo a medio hacer.
  modules: { reports: 'Informes' },

  reports: {
    // Las tarjetas del asistente (backend/assistant.js): guardar y programar.
    assistant: {
      save: {
        action: 'Guardar informe',
        field: { title: 'Nombre', sections: 'Secciones', period: 'Periodo' },
        after: 'Se guardan las preguntas: al abrirlo se recalculan las cifras',
        outcome: 'Informe «{title}» guardado',
      },
      schedule: {
        action: 'Enviar un informe con calendario',
        summary: {
          daily: 'Enviar «{title}» cada día a las {hour}:00',
          weekly: 'Enviar «{title}» cada semana (día {day}) a las {hour}:00',
          monthly: 'Enviar «{title}» cada mes, el día {day}, a las {hour}:00',
          quarterly: 'Enviar «{title}» cada trimestre, el día {day}, a las {hour}:00',
        },
        frequency: { daily: 'Cada día', weekly: 'Cada semana', monthly: 'Cada mes', quarterly: 'Cada trimestre' },
        field: {
          report: 'Informe',
          frequency: 'Cada cuánto',
          weekday: 'Día de la semana (1 = lunes)',
          monthDay: 'Día del mes',
          hour: 'Hora',
          recipients: 'Para',
        },
        you: 'Tú',
        where: 'Informe',
        notSaved: 'No hay ningún informe guardado con ese nombre: guárdalo primero y luego prográmalo',
        asOwner: 'Se calcula con tus permisos cada vez que sale',
        outcome: '«{title}» programado',
      },
    },
    title: 'Informes',
    subtitle: 'Las cifras de toda la casa, y qué significan',
    guide: 'Cada pestaña es un módulo instalado que sabe medir algo. Las cifras las calcula '
      + 'ese módulo, con tus permisos: aquí no hay ninguna copia de sus datos.',

    period: 'Periodo',
    total: 'Total',

    nav: {
      saved: 'Guardados',
      explore: 'Explorar',
    },

    kind: {
      report: 'Informe',
      board: 'Tablero',
    },

    tabs: {
      all: 'Toda la empresa',
    },

    empty: {
      title: 'Todavía no hay nada que medir',
      hint: 'Los informes salen de los módulos instalados. Instala uno —facturación, obras, '
        + 'compras— y su pestaña aparece sola.',
    },

    actions: {
      save: 'Guardar',
      cancel: 'Cancelar',
      edit: 'Editar',
      done: 'Listo',
      remove: 'Quitar',
    },

    fields: {
      title: 'Nombre',
      description: 'Descripción',
      modules: 'De dónde salen las cifras',
      visibility: 'Quién lo ve',
      lastRun: 'Visto por última vez',
    },

    severity: {
      critical: 'Grave',
      warning: 'Aviso',
      info: 'Info',
      good: 'Buena noticia',
    },

    // ── El bloque de arriba: qué dicen las cifras ────────────────────────────
    summary: {
      title: 'Lectura de las cifras',
      subtitle: 'Escrita sobre los números de abajo, y solo sobre ellos',
      generate: 'Leer las cifras',
      again: 'Volver a leerlas',
      working: 'Leyendo las cifras…',
      idleTitle: 'Sin lectura todavía',
      idleHint: 'Cuesta unos segundos y consume presupuesto de IA, así que se pide.',
      quiet: 'Nada destacable en este periodo.',
      caveat: 'Es una lectura de las cifras que tienes debajo, no una cifra más. '
        + 'Los números son el informe; esto es una opinión sobre ellos.',
      evidence: 'En qué se apoya',
      impact: 'Cuánto está en juego',
      cause: 'Posible causa',
      action: 'Qué hacer',
      confidence: {
        high: 'confianza alta',
        medium: 'confianza media',
        low: 'confianza baja',
      },
      unavailable: {
        DISABLED: 'La lectura automática está apagada en los ajustes del módulo.',
        NO_DATA: 'No hay cifras suficientes en este periodo para decir nada.',
        UNREADABLE: 'La respuesta no se pudo leer. Vuelve a intentarlo.',
        AI_PROVIDER_ERROR: 'El proveedor de IA no ha respondido. Las cifras de abajo están bien.',
        generic: 'La lectura no está disponible ahora mismo.',
      },
    },

    // ── Los números ──────────────────────────────────────────────────────────
    kpis: {
      title: 'Las cifras',
      subtitleAll: 'Lo que cada módulo instalado pone por delante',
      subtitleModule: 'Lo que {module} pone por delante',
      empty: 'Ningún módulo ha devuelto cifras para este periodo',
      failed: 'No se han podido leer las cifras de: {modules}',
    },

    changes: {
      title: 'Qué ha cambiado',
      subtitle: 'Ordenado por lo que está en juego, no por el porcentaje',
      emptyTitle: 'Nada se ha movido lo bastante',
      emptyHint: 'El umbral se ajusta en la configuración del módulo.',
    },

    insights: {
      title: 'Qué deberías saber',
      subtitle: 'Lo que los módulos han levantado sin que nadie preguntara',
      emptyTitle: 'Nada que contar',
      emptyHint: 'Es una buena noticia: nada ha cruzado las líneas que pusiste.',
      why: '¿Por qué?',
      acknowledge: 'Ya lo sé',
    },

    focus: {
      subtitle: 'La misma cifra, mes a mes. Pulsa una fila para bajar un nivel.',
      open: 'Abrir en Explorar',
      close: 'Cerrar',
    },

    chart: {
      empty: 'Sin datos que dibujar',
    },

    widget: {
      noRows: 'Sin filas en este periodo',
      nothing: 'Sin calcular',
      failed: 'No se ha podido calcular',
      truncated: 'Se enseñan las primeras {shown} filas. Los totales cubren todo.',
    },

    // ── Los avisos sobre los datos (§43) ─────────────────────────────────────
    quality: {
      generic: 'Aviso sobre los datos: {code}',
      NO_DATA: 'No hay datos en este periodo.',
      SHORT_HISTORY: 'Solo hay {points} periodos de historial: es poco para hablar de tendencia.',
      NOT_ENOUGH_FOR_FORECAST: 'Con {points} periodos no se puede proyectar nada con sentido.',
      MOSTLY_EMPTY: '{empty} de {points} periodos están vacíos.',
      INVOICES_WITHOUT_DUE_DATE: 'El {percent} % de las facturas no tiene fecha de vencimiento, '
        + 'así que no pueden salir como vencidas.',
      INVOICE_LINES_WITHOUT_COST: 'El {percent} % de las facturas no lleva coste en sus líneas: '
        + 'el margen sale mejor de lo que es.',
      QUOTES_LOST_WITHOUT_REASON: 'El {percent} % de los presupuestos perdidos no dice por qué.',
      CUSTOMERS_WITHOUT_SECTOR: 'El {percent} % de los clientes no tiene sector.',
      PROJECTS_WITHOUT_BUDGET: 'El {percent} % de las obras abiertas no tiene coste presupuestado, '
        + 'así que no pueden salir como desviadas.',
      PROJECTS_WITHOUT_COST: 'El {percent} % de las obras abiertas no tiene ningún coste imputado.',
      ORDERS_WITHOUT_EXPECTED_DATE: 'El {percent} % de los pedidos no tiene fecha prevista.',
      MATERIALS_WITHOUT_COST: 'El {percent} % del material con existencias no tiene coste: '
        + 'la valoración se queda corta.',
      ORDERS_WITHOUT_ESTIMATE: 'El {percent} % de las órdenes terminadas no tenía horas estimadas.',
      REFUELS_WITHOUT_PRICE: 'El {percent} % de los repostajes no lleva importe.',
      ATTENDANCE_DAYS_LEFT_OPEN: '{count} jornadas ({percent} %) se quedaron sin cerrar: '
        + 'sus horas se cuentan hasta ahora mismo.',
      ATTENDANCE_PERIOD_TOO_LARGE: 'El periodo tiene más de {limit} fichajes y se ha cortado. '
        + 'Pide un periodo más corto.',
    },

    // ── Los hallazgos que levantan los módulos ───────────────────────────────
    findings: {
      metric_moved: '«{metric}»: {percent} % respecto al periodo anterior.',
      module_insight: 'Un módulo ha levantado un aviso.',
      invoices_overdue: '{count} facturas están vencidas, la más antigua por {days} días '
        + '({amount} €).',
      customer_concentration: '{name} es el {percent} % de lo facturado en el periodo.',
      customers_gone_quiet: '{count} clientes activos llevan meses sin facturar. El mayor es '
        + '{name}, hace {months} meses.',
      quotes_gone_quiet: '{count} presupuestos enviados hace más de un mes siguen sin respuesta '
        + '({amount} €).',
      projects_over_budget: '{count} obras abiertas se han pasado del presupuesto. {code} va un '
        + '{percent} % por encima.',
      supplier_concentration: '{name} es el {percent} % de lo comprado en el periodo.',
      supplier_delivering_late: '{name} está entregando con {days} días de retraso de media, '
        + 'sobre {orders} pedidos.',
      stock_not_moving: '{count} materiales llevan un año sin moverse, por valor de {amount} €.',
      product_over_estimate: '{name} está tardando un {percent} % más de lo estimado, '
        + 'en {orders} órdenes.',
      field_incident_rate: '{rate} incidencias por cada 100 órdenes de trabajo en el periodo.',
      maintenance_overdue: '{count} de {total} planes de mantenimiento están vencidos, '
        + 'el más antiguo por {days} días.',
      asset_repeat_failures: '{name} ha fallado {count} veces en el periodo, con {hours} horas '
        + 'de parada.',
      absences_awaiting_approval: '{count} solicitudes de ausencia esperan respuesta, '
        + 'la más antigua desde hace {days} días.',
      personnel_documents_expired: '{count} documentos de personal están caducados.',
      personnel_documents_expiring: '{count} documentos de personal caducan en un mes, '
        + 'de {people} personas.',
      market_cost_exposure: '{name} se ha movido un {percent} %: unos {amount} € al año '
        + '(confianza {confidence} %).',
      leads_unanswered: '{count} consultas de la web siguen sin contestar, la más antigua desde '
        + 'hace {days} días.',
    },

    // ── El selector de periodo ───────────────────────────────────────────────
    filters: {
      period: 'Periodo',
      from: 'Desde',
      to: 'Hasta',
      compare: 'Comparar con',
    },

    periods: {
      this_month: 'Este mes',
      last_month: 'El mes pasado',
      this_quarter: 'Este trimestre',
      last_quarter: 'El trimestre pasado',
      this_year: 'Este año',
      last_year: 'El año pasado',
      year_to_date: 'Lo que va de año',
      last_7_days: 'Últimos 7 días',
      last_30_days: 'Últimos 30 días',
      last_90_days: 'Últimos 90 días',
      last_6_months: 'Últimos 6 meses',
      last_12_months: 'Últimos 12 meses',
      all_time: 'Todo el histórico',
      custom: 'Entre dos fechas',
    },

    compare: {
      previous_period: 'El periodo anterior',
      previous_year: 'El mismo periodo del año pasado',
      none: 'Con nada',
    },

    visualisations: {
      kpi: 'Una cifra',
      line: 'Línea',
      area: 'Área',
      bar: 'Barras',
      stacked_bar: 'Barras apiladas',
      pie: 'Tarta',
      donut: 'Anillo',
      table: 'Tabla',
    },

    operators: {
      eq: 'es',
      ne: 'no es',
      in: 'es uno de',
      nin: 'no es ninguno de',
      gt: 'mayor que',
      gte: 'mayor o igual que',
      lt: 'menor que',
      lte: 'menor o igual que',
      contains: 'contiene',
      between: 'entre',
      exists: 'tiene valor',
    },

    // ── Explorar ─────────────────────────────────────────────────────────────
    explore: {
      title: 'Explorar',
      subtitle: 'Una pregunta a la vez, sobre lo que sea que tengas instalado',
      guide: 'Elige un tema, las cifras que quieres y por qué agruparlas. Solo aparece lo que '
        + 'puedes ver: lo que no está en las listas, no es tuyo.',
      question: 'La pregunta',
      dataset: 'Sobre qué',
      datasetHint: 'Cada módulo instalado aporta los suyos.',
      grain: 'Cada fila es: {grain}',
      metrics: 'Qué medir',
      metricsHint: 'Sin elegir ninguna salen las que el módulo pone por delante.',
      metricsPlaceholder: 'Escribe y pulsa Intro',
      dimensions: 'Agrupado por',
      dimensionsHint: 'Como mucho tres. Para acotar más, usa un filtro.',
      dimensionsPlaceholder: 'Cliente, sector, comercial…',
      granularity: 'En el tiempo',
      noGranularity: 'Sin eje de tiempo',
      autoVisualisation: 'La que le pegue',
      granularities: {
        auto: 'Automático',
        day: 'Por día',
        week: 'Por semana',
        month: 'Por mes',
        quarter: 'Por trimestre',
        year: 'Por año',
      },
      dateField: 'Qué fecha manda',
      dateFieldHint: 'Cambia lo que significa «en julio».',
      visualisation: 'Cómo se dibuja',
      limit: 'Cuántas filas',
      filters: 'Filtros',
      filtersHint: 'Los totales siguen cubriendo todo lo que pase el filtro.',
      addFilter: 'Añadir filtro',
      removeFilter: 'Quitar',
      value: 'Valor',
      run: 'Calcular',
      save: 'Guardar como informe',
      saveTitle: 'Guardar la pregunta',
      saved: 'Guardado',
      answerBy: '{metrics} por {dimension}',
      rowCount: '{count} filas',
      reading: 'Qué forma tiene',
      readingHint: 'Tendencia, valores raros y proyección, cada uno con lo seguro que es',
      readingIdle: 'Sin analizar todavía',
      analyse: 'Analizar',
      trend: 'Tendencia',
      trendUnclear: 'No se ve una tendencia clara con {points} periodos.',
      trends: {
        up: 'Sube un {percent} % por periodo (ajuste del {fit} %).',
        down: 'Baja un {percent} % por periodo (ajuste del {fit} %).',
        flat: 'Plana.',
        unknown: 'Sin datos suficientes.',
      },
      anomalies: 'Periodos raros',
      forecast: 'Proyección',
      forecastNone: 'No hay historial suficiente para proyectar nada.',
      forecastNext: 'El próximo periodo: {value} (entre {low} y {high}, {confidence}).',
      confidence: {
        high: 'confianza alta',
        medium: 'confianza media',
        low: 'confianza baja',
        insufficient: 'sin base',
      },
      caveats: 'Con qué cuidado',
    },

    // ── Los guardados ────────────────────────────────────────────────────────
    saved: {
      title: 'Informes guardados',
      subtitle: 'Las preguntas que alguien quiso volver a hacerse',
      search: 'Buscar',
      searchPlaceholder: 'Por nombre, descripción o etiqueta',
      module: 'De qué módulo',
      allModules: 'De todos',
      empty: 'Nada guardado todavía',
      never: 'Nunca',
      template: 'Plantilla',
      company: 'Toda la empresa',
      shared: 'Compartido',
      private: 'Solo yo',
      scheduled: 'Programado',
      scopes: {
        all: 'Todos',
        mine: 'Míos',
        shared: 'Compartidos conmigo',
        templates: 'Plantillas',
        scheduled: 'Programados',
      },
    },

    // ── Un informe ───────────────────────────────────────────────────────────
    report: {
      loading: 'Abriendo el informe…',
      periodHint: 'Cambiar el periodo aquí no cambia el informe: es una mirada, no una decisión.',
      narrate: 'Escribir las conclusiones',
      export: 'Exportar',
      schedule: 'Programar',
      saved: 'Guardado',
      editing: 'Añadir al informe',
      sectionTitle: 'Título de la sección',
      addText: 'Un texto',
      addNarrative: 'Conclusiones de IA',
      addInsights: 'Los hallazgos',
      addQuery: 'Una pregunta nueva',
      conclusions: 'Conclusiones',
      conclusionsHint: 'Escritas sobre las cifras de esta sección',
      conclusionsIdle: 'Pulsa «Escribir las conclusiones» arriba',
      emptyTitle: 'El informe está vacío',
      emptyHint: 'Añade una pregunta desde Explorar, o un texto desde aquí.',
      formats: {
        pdf: 'PDF',
        docx: 'Word',
        csv: 'CSV',
      },
    },

    schedule: {
      title: 'Programar el informe',
      hint: 'Sale solo, a la hora que digas, y llega por correo a gente que puede no tener '
        + 'cuenta aquí. Se calcula con TUS permisos.',
      enabled: 'Enviarlo solo',
      frequency: 'Cada cuánto',
      frequencies: {
        daily: 'Cada día',
        weekly: 'Cada semana',
        monthly: 'Cada mes',
        quarterly: 'Cada trimestre',
      },
      weekday: 'Qué día de la semana (1 = lunes)',
      monthday: 'Qué día del mes',
      hour: 'A qué hora (UTC)',
      recipients: 'A qué direcciones',
      recipientsHint: 'Gente de fuera: el consejo, la gestoría.',
      recipientsPlaceholder: "correo{'@'}ejemplo.com",
      roles: 'Y a quien tenga estos roles',
      rolesHint: 'Sigue funcionando cuando alguien se va.',
      format: 'En qué formato',
      saved: 'Programado',
    },

    export: {
      index: 'Índice',
      generated: 'Generado',
    },

    // ── Todo sobre uno ───────────────────────────────────────────────────────
    entity: {
      subtitle: 'Lo que cada módulo instalado sabe de este registro',
      emptyTitle: 'Ningún módulo sabe nada de esto',
      emptyHint: 'O no hay módulos instalados que lo midan, o no tienes permiso para verlos.',
      failed: 'Este módulo no ha podido responder.',
      open: 'Abrir',
      footnote: 'Cada bloque lo calcula su módulo, con tus permisos. Aquí no se guarda ninguna cifra.',
      kinds: {
        customer: 'Cliente',
        supplier: 'Proveedor',
        project: 'Obra',
        user: 'Persona',
        material: 'Material',
        asset: 'Máquina',
        site: 'Centro',
        team: 'Equipo',
        vehicle: 'Vehículo',
      },
    },

    // ── Lo que aporta al inicio (§15.4) ──────────────────────────────────────
    dashboard: {
      findings: 'Avisos abiertos',
      findingsHelp: 'Cuántos hallazgos graves o de aviso están sin marcar como vistos',
      findingsList: 'Qué deberías saber',
      findingsListHelp: 'Los últimos hallazgos que los módulos han levantado solos',
      mine: 'Mis informes',
      mineHelp: 'Los informes que has guardado tú, por orden de última modificación',
    },

    related: {
      figures: 'Cifras',
    },

    notifications: {
      categories: {
        insight: 'Hallazgos del análisis',
      },
      insights: {
        title: 'Hay algo que mirar',
        body: 'El análisis de esta noche ha levantado {count} avisos.',
      },
    },

    // ── Lo que este módulo sabe medir de sí mismo ────────────────────────────
    analytics: {
      usage: {
        label: 'Informes guardados',
        help: 'Una fila por informe guardado, con cuántas veces se ha abierto',
      },
      dates: {
        created: 'Cuándo se guardó',
      },
      metrics: {
        count: 'Informes',
        runs: 'Veces abiertos',
      },
      dimensions: {
        owner: 'Quién lo guardó',
        kind: 'Informe o tablero',
      },
    },
  },

  // ── Las pantallas de la plataforma que hablan de este módulo ───────────────
  permissions: {
    reports: {
      read: 'Abrir Informes y hacer preguntas. No da acceso a ninguna cifra: cada número lo '
        + 'calcula su módulo con los permisos de quien pregunta',
      write: 'Guardar informes y tableros propios',
      share: 'Dar un informe a otra persona. Compartir nunca amplía lo que el otro puede ver',
      schedule: 'Poner un informe en un reloj para que salga por correo solo',
      manage: 'Editar y borrar informes de cualquiera, y publicar plantillas',
    },
  },

  audit: {
    actions: {
      report: {
        created: 'Informe creado',
        updated: 'Informe modificado',
        deleted: 'Informe borrado',
        exported: 'Informe exportado',
      },
    },
  },

  settings: {
    ns: {
      reports: {
        // El nombre de la sección en la pantalla de Ajustes.
        title: 'Informes',

        alertThresholdPct: {
          label: 'Avisar cuando una cifra se mueva más de esto',
          help: 'Contra el periodo anterior. Más bajo es más avisos y más ruido; una empresa con '
            + 'un mes tranquilo al 5 % deja de leerlos.',
        },
        alertMinImpact: {
          label: 'Y solo si el cambio vale al menos esto',
          help: 'En euros, para las cifras que van en euros. Un 40 % sobre 300 € es aritmética, '
            + 'no una noticia. 0 quiere decir que cuenta cualquier cambio que pase el porcentaje.',
        },
        alertPeriod: {
          label: 'Qué periodo mira el análisis de cada noche',
          help: 'Y se compara con el inmediatamente anterior.',
        },
        alertRoles: {
          label: 'A quién se avisa cuando se encuentra algo importante',
          help: 'Roles, no personas, para que siga funcionando cuando alguien se va. '
            + 'Solo lo grave y los avisos; el resto espera en la pantalla.',
        },
        aiSummary: {
          label: 'Dejar que el asistente escriba la lectura de las cifras',
          help: 'El bloque de arriba de cada pestaña, y las conclusiones de un informe '
            + 'exportado. Apagado son los números y los hallazgos, sin nadie escribiendo sobre '
            + 'ellos. Consume presupuesto de IA cada vez que corre.',
        },
        overviewDatasets: {
          label: 'Cuántos temas cubre la pestaña de resumen',
          help: 'La pestaña de toda la empresa pregunta a cada módulo instalado por sus cifras '
            + 'principales. Con quince módulos eso es una pantalla lenta; cada módulo sigue '
            + 'teniendo su propia pestaña.',
        },
        overviewCharts: {
          label: 'Cuántas gráficas dibuja la pestaña de resumen',
          help: 'Las cifras vienen con su propia historia y dibujarla no cuesta nada más. Lo que sí '
            + 'cuesta es la pantalla: pasada la media docena, un resumen deja de serlo. Cero deja '
            + 'las fichas y los hallazgos, con cada gráfica a un clic.',
        },
      },
    },

    mail: {
      reports__scheduled_report: {
        label: 'Informes programados',
        help: 'El buzón desde el que sale el informe que se manda solo, con su PDF adjunto.',
      },
    },
  },

  assistant: {
    openers: {
      reports: {
        how_is_it_going: '¿Cómo va la empresa este mes?',
        what_changed: '¿Qué ha cambiado desde el mes pasado?',
        worry: '¿Qué debería preocuparme?',
      },
    },
  },
}
